import { useState, /*useRef,*/ useEffect } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import ComponentTable from '../../../Components/Table/Index';
import { Column } from '../../../Components/Table/Types';
import * as Icon from '@mui/icons-material/';
import { useNavigate } from 'react-router-dom';
import { Form, Formik, Field } from 'formik';
import { IconButton } from '@mui/material';
import { Loader } from '../../../../shared/components/Loader';
import { HeaderComponent } from '../../../../shared/components/HeaderComponent';
import { Home } from '../../../../routes/RoutesNames';
import { ButtonMenu } from '../../../../shared/components/ButtonMenu';
import BoletoController from '../../../../controllers/BoletoController';
import { toast } from 'react-toastify';
import { formatarData } from '../../../../shared/utils/helpers/date';
import { Button } from '../../../../shared/components/button/Button';
import Status from '../../../Components/Status/Index';

interface Row {
  cod: number;
  data_lancamento: string;
  origem_id: string;
  origem: string;
  valor_total: string;
  dinheiro: string;
  pix: string;
  status_pix: string;
  deposito: string;
  cheque: string;
  credito: string;
  debito: string;
  caixa_id: string;
  obs: string;
}

const dateFormat = (d: string) => d.replaceAll('.', '/')
const dateReformat = (d: string) => d.split('-').reverse().join('.')

const msPerDay = 24 * 60 * 60 * 1000;
const diffInDays = (d1: Date, d2: Date) => (d1 - d2) / msPerDay

const formatMoney = (value: number, decimals = 2, locale = 'pt-BR'): string => {
  if (typeof value !== 'number') {
    value = Number(value);
  }
  if (!Number.isFinite(value)) return '';

  return `R$ ${new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value)}`;
}

const getStatusColor = (s: string) => ({
  'liquidado': '#4caf50',
  'baixado': '#42a5f5',
  'protestado': '#ef5350'
})[s.toLowerCase()] || '#999'

let lastFormValues = {}

export default function BoletoPesquisar() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const tableColumns: Column[] = [
    // [SICOOB 30/09/2026] A lista passou a ter as duas carteiras. Sem esta coluna, duas
    // linhas identicas na tela poderiam ser de bancos diferentes - e o numero do boleto
    // sozinho nao diz de qual. As demais colunas mantiveram os nomes do Banco do Brasil,
    // que e o formato sobre o qual esta tela foi construida; o backend traduz o retorno do
    // Sicoob para eles.
    { id: 'instituicao', label: 'Instituição', minWidth: 0, align: 'center' },
    { id: 'numeroBoletoBB', label: 'Boleto', minWidth: 0, align: 'center' },
    { id: 'dataRegistro', label: 'Data Registro', minWidth: 0, align: 'center' },
    { id: 'dataVencimento', label: 'Data Vencimento', minWidth: 0, align: 'center' },
    { id: 'valorOriginal', label: 'Valor Original', minWidth: 0, align: 'left' },
    { id: 'carteiraConvenio', label: 'Carteira Convênio', minWidth: 0, align: 'left' },
    { id: 'variacaoCarteiraConvenio', label: 'Variação Convênio', minWidth: 0, align: 'left' },
    { id: 'codigoEstadoTituloCobranca', label: 'Código Cobrança', minWidth: 0, align: 'left' },
    { id: 'estadoTituloCobranca', label: 'Estado Cobrança', minWidth: 0, align: 'center' },
    { id: 'contrato', label: 'Contrato', minWidth: 0, align: 'left' },
    { id: 'dataMovimento', label: 'Data Movimento', minWidth: 0, align: 'center' },
    { id: 'dataCredito', label: 'Data Crédito', minWidth: 0, align: 'center' },
    { id: 'valorAtual', label: 'Valor Atual', minWidth: 0, align: 'left' },
    { id: 'valorPago', label: 'Valor Pago', minWidth: 0, align: 'left' },
    { id: 'BtnMenu', label: '', width: 60, minWidth: 0, align: 'right' }
  ];

  const [tableRows, setTableRows] = useState<Row[]>([]);
  const [total, setTotal] = useState<any>({});

  const handleNavigation = async (): Promise<void> => {
    navigate(Home, {replace: true})
  };

  const handleEditItem = (item: Row, duploClick = false, duploClickId = 0) => {
    //
  };

  const handleDeleteItem = async (cli_id: number) => {
    //
  };

  const handleSubmit = async (values: any) => {
    // const r = await BoletoController.Gerar(106);
    lastFormValues = values
    if (diffInDays(new Date(values.dataFimRegistro), new Date(values.dataInicioRegistro)) > 30) return toast("A diferença da Data de Início e Data de Fim deve ser no máximo de 30 dias", { type: 'error' })
    const res = await BoletoController.Listar({ dataFimRegistro: dateReformat(values.dataFimRegistro), dataInicioRegistro: dateReformat(values.dataInicioRegistro), liquidar: !!values.liquidar });
    const data = (res?.Data?.boletos || (res.StatusCode && res.StatusCode >= 200 && res.StatusCode < 300 && res?.Data?.length > 0 ? res.Data : []) || []).map((b: any) => ({ 
      ...b,
      dataRegistro: dateFormat(b.dataRegistro), 
      dataVencimento: dateFormat(b.dataVencimento),
      dataMovimento: dateFormat(b.dataMovimento), 
      dataCredito: dateFormat(b.dataCredito),
      valorOriginal: formatMoney(b.valorOriginal),
      valorAtual: formatMoney(b.valorAtual),
      valorPago: formatMoney(b.valorPago),
      estadoTituloCobranca: b?.estadoTituloCobranca && (
        <Status text={b?.estadoTituloCobranca || ''} color={getStatusColor(b?.estadoTituloCobranca || '')} />
      ),
      BtnMenu: (
        <ButtonMenu
            items={[
                { onClick: () => handleEditItem(p), menu: 'Ver Boleto' },
            ]}
        />
    )}))
    if(res.Data?.erros?.length) return toast((res.Data?.erros || []).map(e => e?.textoMensagem || '').join(' '), { type: 'error' })
    setTableRows(data);
  };

  const today = new Date();
  const initialValues = {
    dataFimRegistro: formatarData(today),
    dataInicioRegistro: formatarData(today.setDate(today.getDate() - 3) && today)
  };

  useEffect(() => {
    handleSubmit(initialValues);
  }, []);

  return (
    <Loader loading={loading}>
      <Box
        component='div'
        sx={{
          '& .MuiTextField-root': { m: 1, width: '100%' },
        }}
      >
        <div className={'pb-2'}>
          <HeaderComponent
            className={'mb-6'}
            text={'Boletos'}
            ButtonRight={
              <Button
                className={''}
                variant='success'
                onClick={() => { 
                  handleSubmit({ ...lastFormValues, liquidar: true })
                }}
                iconStart={<Icon.Refresh />}
                title={'Liquidar'}
              />
            }
          />
          <Box mb={2} />

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {({ errors, setFieldValue }) => (
              <Form className={''}>
                <Box className={'grid grid-cols-12 gap-4 w-full'}>
                  <Box className={'col-span-2'}>
                    <Field
                      name='dataInicioRegistro'
                      as={TextField}
                      label='Data Início Registro'
                      InputLabelProps={{ shrink: true }}
                      type='date'
                    />
                  </Box>
                  <Box className={'col-span-2'}>
                    <Field
                      name='dataFimRegistro'
                      as={TextField}
                      label='Data Fim Registro'
                      InputLabelProps={{ shrink: true }}
                      type='date'
                    />
                  </Box>
                  <Box className={'pt-1'}>
                    <IconButton size={'large'} type={'submit'}>
                      <Icon.Search style={{ fontSize: '28px' }} />
                    </IconButton>
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </div>
        <ComponentTable columns={tableColumns} rows={tableRows} tableHeight={'max(50vh, 600px)'} style={{ padding: '100px 0' }} onRowDoubleClick={(row) => {
          handleEditItem(row, true, row.Id);
        }} />
      </Box>
    </Loader>
  );
}
